<template>
  <div class="zone-parts-list">
    <!-- Search/Filter -->
    <div class="search-box">
      <input
        type="text"
        class="form-control"
        v-model="searchQuery"
        placeholder="Search zones or sectors..."
      >
    </div>

    <!-- Zone Tree -->
    <div class="zone-tree" v-if="!loading">
      <div v-if="filteredZones.length === 0" class="no-results">
        <p>No zones found</p>
      </div>

      <div
        v-for="zone in filteredZones"
        :key="zone.id"
        class="zone-item"
        :class="{ 'expanded': expandedZones.includes(zone.id) }"
      >
        <div
          class="zone-header"
          @click="toggleZone(zone)"
          :class="{ 'selected': selectedFeature?.id === zone.id && selectedFeature?.type === 'zone' }"
        >
          <i
            class="fa expand-icon"
            :class="expandedZones.includes(zone.id) ? 'fa-chevron-down' : 'fa-chevron-right'"
          ></i>
          <i class="fa fa-folder zone-icon"></i>
          <span class="zone-name">{{ zone.name }}</span>
          <span class="zone-count">({{ getZonepartCount(zone.id) }})</span>
          
          <div class="zone-actions">
            <button
              class="btn-icon"
              @click.stop="toggleZoneVisibility(zone)"
              :title="zone.visible ? 'Hide zone' : 'Show zone'"
            >
              <i class="fa" :class="zone.visible ? 'fa-eye' : 'fa-eye-slash'"></i>
            </button>
          </div>
        </div>

        <!-- Zoneparts -->
        <transition name="slide">
          <div v-if="expandedZones.includes(zone.id)" class="zoneparts-container">
            <div
              v-for="zonepart in getZonepartsByZone(zone.id)"
              :key="zonepart.id"
              class="zonepart-row"
            >
              <div
                class="zonepart-item"
                :class="{
                  'selected': selectedFeature?.id === zonepart.id && selectedFeature?.type === 'zonepart',
                  'has-geometry': !!zonepart.geometry,
                  'has-parts': isMultiPolygon(zonepart)
                }"
                @click="selectZonepart(zonepart)"
              >
                <i
                  v-if="isMultiPolygon(zonepart)"
                  class="fa expand-icon part-chevron"
                  :class="isSectorExpanded(zonepart) ? 'fa-chevron-down' : 'fa-chevron-right'"
                  @click.stop="toggleSectorExpansion(zonepart)"
                ></i>
                <i class="fa fa-map zonepart-icon"></i>
                <span class="zonepart-name">{{ zonepart.name || `Sector ${zonepart.id}` }}</span>
                <span v-if="isMultiPolygon(zonepart)" class="part-count">({{ partCount(zonepart) }} parts)</span>

                <div class="zonepart-actions">
                  <button
                    v-if="zonepart.geometry"
                    class="btn-icon"
                    @click.stop="zoomToZonepart(zonepart)"
                    title="Zoom to sector"
                  >
                    <i class="fa fa-search-plus"></i>
                  </button>
                  <button
                    class="btn-icon"
                    @click.stop="editZonepartName(zonepart)"
                    title="Edit name"
                  >
                    <i class="fa fa-pencil"></i>
                  </button>
                </div>
              </div>

              <transition name="slide">
                <div
                  v-if="isMultiPolygon(zonepart) && isSectorExpanded(zonepart)"
                  class="parts-container"
                >
                  <div
                    v-for="(_, partIndex) in zonepart.geometry.coordinates"
                    :key="`${zonepart.id}-part-${partIndex}`"
                    class="part-item"
                    :class="{ 'selected': isPartSelected(zonepart, partIndex) }"
                    @click.stop="$emit('select-part', { zonepartId: zonepart.id, partIndex })"
                    :data-cy="`zonepart-${zonepart.id}-part-${partIndex}`"
                  >
                    <i class="fa fa-map-o part-icon"></i>
                    <span class="part-name">Part {{ partIndex + 1 }}</span>
                    <div class="part-actions">
                      <button
                        class="btn-icon"
                        @click.stop="$emit('delete-part', { zonepartId: zonepart.id, partIndex })"
                        :title="`Delete Part ${partIndex + 1}`"
                        :data-cy="`delete-part-${zonepart.id}-${partIndex}`"
                      >
                        <i class="fa fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </transition>
            </div>

            <div v-if="getZonepartsByZone(zone.id).length === 0" class="no-zoneparts">
              No sectors in this zone
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- Loading spinner -->
    <div v-if="loading" class="loading">
      <i class="fa fa-spinner fa-spin"></i> Loading zones...
    </div>

    <!-- Quick Actions -->
    <div class="list-actions">
      <button class="btn btn-sm btn-default" @click="expandAll">
        <i class="fa fa-expand"></i> Expand All
      </button>
      <button class="btn btn-sm btn-default" @click="collapseAll">
        <i class="fa fa-compress"></i> Collapse All
      </button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

export default {
  name: 'ZonePartsList',
  props: {
    schoolId: {
      type: Number,
      required: true
    },
    onFeatureSelect: {
      type: Function,
      default: () => {}
    },
    // Index of the sub-selected polygon part of the currently selected MultiPolygon sector,
    // or null when no part is sub-selected. Owned by the parent (ServiceAreaMap.vue).
    selectedPartIndex: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      searchQuery: '',
      expandedZones: [],
      expandedSectors: [], // zonepart IDs that have their parts list expanded
      loading: true,
      visibleZones: new Set()
    };
  },
  computed: {
    ...mapState('serviceAreas', ['zones', 'zoneparts', 'selectedFeature']),
    ...mapGetters('serviceAreas', ['getZonepartsByZoneId']),
    
    filteredZones() {
      if (!this.searchQuery) {
        return this.zones;
      }
      
      const query = this.searchQuery.toLowerCase();
      return this.zones.filter(zone => {
        // Check zone name
        if (zone.name.toLowerCase().includes(query)) {
          return true;
        }
        
        // Check zoneparts in this zone
        const zoneparts = this.getZonepartsByZone(zone.id);
        return zoneparts.some(zp => 
          (zp.name || `Sector ${zp.id}`).toLowerCase().includes(query)
        );
      });
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    ...mapActions('serviceAreas', [
      'fetchZones',
      'fetchZoneparts',
      'selectFeature',
      'updateZonepart',
      'setActiveTool'
    ]),

    async loadData() {
      try {
        await Promise.all([
          this.fetchZones(this.schoolId),
          this.fetchZoneparts(this.schoolId)
        ]);

        // Initially expand zones with zoneparts
        this.zones.forEach(zone => {
          if (this.getZonepartCount(zone.id) > 0) {
            if (!this.expandedZones.includes(zone.id)) {
              this.expandedZones.push(zone.id);
            }
            this.visibleZones.add(zone.id);
          }
        });

        this.loading = false;
      } catch (error) {
        console.error('Failed to load zones data:', error);
        this.loading = false;
      }
    },

    getZonepartsByZone(zoneId) {
      return this.getZonepartsByZoneId(zoneId);
    },

    getZonepartCount(zoneId) {
      return this.getZonepartsByZone(zoneId).length;
    },

    toggleZone(zone) {
      const index = this.expandedZones.indexOf(zone.id);
      if (index > -1) {
        this.expandedZones.splice(index, 1);
      } else {
        this.expandedZones.push(zone.id);
      }

      // Also select the zone
      this.selectZone(zone);
    },

    selectZone(zone) {
      const feature = {
        id: zone.id,
        type: 'zone',
        name: zone.name,
        data: zone
      };

      this.selectFeature(feature);
      this.onFeatureSelect(feature);
    },

    selectZonepart(zonepart) {
      // First, set the active tool to 'select'
      this.setActiveTool('select');

      // Then select the zonepart
      const feature = {
        id: zonepart.id,
        type: 'zonepart',
        name: zonepart.name || `Sector ${zonepart.id}`,
        data: zonepart
      };

      this.selectFeature(feature);
      this.onFeatureSelect(feature);

      // Finally, zoom to the zonepart if it has geometry
      if (zonepart.geometry) {
        this.zoomToZonepart(zonepart);
      }
    },
    
    toggleZoneVisibility(zone) {
      zone.visible = !zone.visible;
      if (zone.visible) {
        this.visibleZones.add(zone.id);
      } else {
        this.visibleZones.delete(zone.id);
      }
      
      // Emit event to update map
      this.$emit('visibility-change', {
        zoneId: zone.id,
        visible: zone.visible
      });
    },
    
    zoomToZonepart(zonepart) {
      this.$emit('zoom-to', zonepart);
    },
    
    editZonepartName(zonepart) {
      // Emit event to parent to handle modal in map container
      this.$emit('edit-zonepart-name', zonepart);
    },
    
    expandAll() {
      this.expandedZones = this.zones.map(zone => zone.id);
    },

    collapseAll() {
      this.expandedZones = [];
    },

    // --- MultiPolygon part helpers ---
    isMultiPolygon(zonepart) {
      return zonepart?.geometry?.type === 'MultiPolygon';
    },
    partCount(zonepart) {
      return this.isMultiPolygon(zonepart) ? zonepart.geometry.coordinates.length : 0;
    },
    isSectorExpanded(zonepart) {
      return this.expandedSectors.includes(zonepart.id);
    },
    toggleSectorExpansion(zonepart) {
      const i = this.expandedSectors.indexOf(zonepart.id);
      if (i >= 0) {
        this.expandedSectors.splice(i, 1);
      } else {
        this.expandedSectors.push(zonepart.id);
      }
    },
    isPartSelected(zonepart, partIndex) {
      return this.selectedFeature?.id === zonepart.id
          && this.selectedFeature?.type === 'zonepart'
          && this.selectedPartIndex === partIndex;
    }
  },
  watch: {
    zoneparts: {
      handler(newVal, oldVal) {
        // Auto-expand the parent zone of any newly added zonepart (existing behavior)
        if (oldVal && newVal.length > oldVal.length) {
          const oldIds = new Set(oldVal.map(zp => zp.id));
          for (const zp of newVal) {
            if (!oldIds.has(zp.id) && zp.zone_id && !this.expandedZones.includes(zp.zone_id)) {
              this.expandedZones.push(zp.zone_id);
            }
          }
        }
        // Prune expandedSectors entries whose geometry is no longer MultiPolygon
        // (e.g. after deleting a part collapsed it to Polygon, or the sector was deleted)
        this.expandedSectors = this.expandedSectors.filter(id => {
          const zp = newVal.find(z => z.id === id);
          return zp && this.isMultiPolygon(zp);
        });
      },
      deep: true
    },
    selectedFeature(newVal) {
      // Auto-expand a multipolygon sector's parts list the first time it becomes selected,
      // so the part-management affordance is visible without requiring an extra click.
      if (newVal?.type !== 'zonepart') return;
      const zp = this.zoneparts.find(z => z.id === newVal.id);
      if (zp && this.isMultiPolygon(zp) && !this.expandedSectors.includes(zp.id)) {
        this.expandedSectors.push(zp.id);
      }
    },
    searchQuery(newQuery) {
      // Auto-expand zones that have matching zoneparts
      if (newQuery) {
        const query = newQuery.toLowerCase();
        this.zones.forEach(zone => {
          const zoneparts = this.getZonepartsByZone(zone.id);
          const hasMatch = zoneparts.some(zp => 
            (zp.name || `Sector ${zp.id}`).toLowerCase().includes(query)
          );
          
          if (hasMatch && !this.expandedZones.includes(zone.id)) {
            this.expandedZones.push(zone.id);
          }
        });
      }
    }
  }
};
</script>

<style scoped>
.zone-parts-list {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.search-box {
  padding: 10px;
  border-bottom: 1px solid #ddd;
}

.zone-tree {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.zone-item {
  margin-bottom: 4px;
}

.zone-header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.zone-header:hover {
  background: #e9ecef;
}

.zone-header.selected {
  background: #d1ecf1;
  border-color: #bee5eb;
}

.expand-icon {
  margin-right: 8px;
  font-size: 10px;
  width: 10px;
}

.zone-icon {
  margin-right: 8px;
  color: #6c757d;
}

.zone-name {
  flex: 1;
  font-weight: 500;
}

.zone-count {
  margin-left: 8px;
  color: #6c757d;
  font-size: 12px;
}

.zone-actions {
  display: flex;
  gap: 4px;
}

.btn-icon {
  background: none;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  color: #6c757d;
  transition: color 0.2s;
}

.btn-icon:hover {
  color: #495057;
}

.zoneparts-container {
  margin-left: 20px;
  margin-top: 4px;
}

.zonepart-item {
  display: flex;
  align-items: center;
  padding: 6px 12px;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 3px;
  margin-bottom: 2px;
  cursor: pointer;
  transition: all 0.2s;
}

.zonepart-item:hover {
  background: #f8f9fa;
}

.zonepart-item.selected {
  background: #d4edda;
  border-color: #c3e6cb;
}

.zonepart-item.has-geometry .zonepart-icon {
  color: #28a745;
}

.zonepart-icon {
  margin-right: 8px;
  color: #adb5bd;
  font-size: 14px;
}

.zonepart-name {
  flex: 1;
  font-size: 14px;
}

.zonepart-actions {
  display: flex;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.2s;
}

.zonepart-item:hover .zonepart-actions {
  opacity: 1;
}

.no-zoneparts {
  padding: 12px;
  text-align: center;
  color: #6c757d;
  font-style: italic;
  font-size: 13px;
}

.no-results {
  text-align: center;
  padding: 40px;
  color: #6c757d;
}

.loading {
  text-align: center;
  padding: 40px;
  color: #6c757d;
}

.list-actions {
  padding: 10px;
  border-top: 1px solid #ddd;
  display: flex;
  gap: 8px;
}

.slide-enter-active, .slide-leave-active {
  transition: all 0.2s;
}

.slide-enter, .slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* MultiPolygon part rows --- nested under a sector */
.zonepart-row {
  margin-bottom: 2px;
}

.zonepart-item .part-chevron {
  margin-right: 4px;
  color: #6c757d;
  cursor: pointer;
  padding: 2px 4px;
}

.zonepart-item .part-chevron:hover {
  color: #495057;
}

.part-count {
  margin-left: 6px;
  margin-right: 6px;
  color: #6c757d;
  font-size: 12px;
  font-style: italic;
}

.parts-container {
  margin-left: 24px;
  margin-top: 2px;
  margin-bottom: 4px;
}

.part-item {
  display: flex;
  align-items: center;
  padding: 4px 10px;
  background: #fdfdfd;
  border: 1px solid #f0f0f0;
  border-radius: 3px;
  margin-bottom: 2px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
}

.part-item:hover {
  background: #f8f9fa;
}

.part-item.selected {
  background: #d4edda;
  border-color: #c3e6cb;
}

.part-icon {
  margin-right: 8px;
  color: #adb5bd;
  font-size: 12px;
}

.part-name {
  flex: 1;
}

.part-actions {
  display: flex;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.2s;
}

.part-item:hover .part-actions,
.part-item.selected .part-actions {
  opacity: 1;
}
</style>